import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, HOUSE_DOCUMENT } from '../constants/documentTheme';
import { AI_SEARCH_STEPS, createEmptyAiSearchProgress } from '../utils/aiSearchProgress';

const STEP_META = [
  { key: 'tripInfo', labelKey: 'partnerAi.searchStepTrip', pin: null },
  { key: 'hotels', labelKey: 'partnerAi.searchStepHotels', pin: 'H', color: '#8B4513', x: '28%', y: '34%' },
  { key: 'restaurants', labelKey: 'partnerAi.searchStepDining', pin: 'R', color: '#C45C26', x: '62%', y: '28%' },
  { key: 'locations', labelKey: 'partnerAi.searchStepPlaces', pin: 'P', color: '#2F6B4F', x: '46%', y: '58%' },
  { key: 'activities', labelKey: 'partnerAi.searchStepActivities', pin: 'A', color: '#1F4E79', x: '72%', y: '62%' }
];

const searchKeyframes = {
  '@keyframes partnerAiSearchFadeIn': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  '@keyframes partnerAiSearchRing': {
    '0%': { transform: 'scale(0.4)', opacity: 0.55 },
    '70%': { opacity: 0.18 },
    '100%': { transform: 'scale(5.2)', opacity: 0 }
  },
  '@keyframes partnerAiSearchSweep': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  },
  '@keyframes partnerAiSearchBeacon': {
    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.25)', opacity: 0.75 }
  },
  '@keyframes partnerAiSearchPin': {
    '0%, 100%': { transform: 'translateY(0) scale(1)', opacity: 0.85 },
    '50%': { transform: 'translateY(-5px) scale(1.08)', opacity: 1 }
  }
};

const stepStatusLabel = (t, stepKey, status, count) => {
  if (status === 'done') {
    if (stepKey === 'tripInfo') return t('partnerAi.searchStepDoneTrip');
    return t('partnerAi.searchStepFound', { count: count || 0 });
  }
  if (status === 'active') return t('partnerAi.searchStepActive');
  return t('partnerAi.searchStepWaiting');
};

/**
 * Searching state for Suggest stops — radar plus live per-category progress
 * driven by PremiumAIService onProgress events.
 */
const PartnerAiSearchingPanel = ({
  destinationLabel = '',
  progress = createEmptyAiSearchProgress()
}) => {
  const { t } = useTranslation();

  const { doneCount, percent, activeLabel, steps } = useMemo(() => {
    const rows = STEP_META.map((meta) => {
      const status = progress?.[meta.key] || 'pending';
      const count = progress?.counts?.[meta.key];
      return { ...meta, status, count };
    });
    const done = rows.filter((row) => row.status === 'done').length;
    const active = rows.find((row) => row.status === 'active');
    return {
      steps: rows,
      doneCount: done,
      percent: Math.round((done / AI_SEARCH_STEPS.length) * 100),
      activeLabel: active
        ? t(active.labelKey)
        : (done === rows.length ? t('partnerAi.searchingFinishing') : t('partnerAi.searchingHeading'))
    };
  }, [progress, t]);

  return (
    <Box
      data-testid="partner-trip-ai-searching"
      sx={{
        ...searchKeyframes,
        mb: 2.5,
        p: { xs: 2.5, md: 3 },
        border: `1px solid ${DOCUMENT.ruleHard}`,
        bgcolor: DOCUMENT.canvas,
        overflow: 'hidden',
        animation: 'partnerAiSearchFadeIn 0.45s ease-out'
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink3,
          mb: 1
        }}
      >
        {t('partnerAi.searchingHeading')}
      </Typography>
      <Typography
        sx={{
          fontFamily: DOCUMENT.display,
          fontSize: { xs: 20, md: 22 },
          color: DOCUMENT.ink,
          mb: 0.5,
          minHeight: 28
        }}
        data-testid="partner-trip-ai-searching-status"
      >
        {activeLabel}
      </Typography>
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>
        {destinationLabel
          ? t('partnerAi.searchingNear', { place: destinationLabel })
          : t('partnerAi.searchingHint')}
      </Typography>

      <Box sx={{ mb: 2 }} data-testid="partner-trip-ai-searching-bar">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
            {t('partnerAi.searchingProgress', { done: doneCount, total: AI_SEARCH_STEPS.length })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{percent}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: DOCUMENT.rule,
            '& .MuiLinearProgress-bar': {
              bgcolor: HOUSE_DOCUMENT.oxblood,
              borderRadius: 999
            }
          }}
        />
      </Box>

      <Box
        data-testid="partner-trip-ai-searching-steps"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}
      >
        {steps.map((step) => {
          const isDone = step.status === 'done';
          const isActive = step.status === 'active';
          return (
            <Box
              key={step.key}
              data-testid={`partner-trip-ai-search-step-${step.key}`}
              data-status={step.status}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 1.25,
                py: 1,
                border: `1px solid ${isActive ? HOUSE_DOCUMENT.accent : DOCUMENT.rule}`,
                bgcolor: isDone ? DOCUMENT.ivory : DOCUMENT.surface,
                opacity: step.status === 'pending' ? 0.72 : 1,
                transition: 'border-color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: isDone ? HOUSE_DOCUMENT.oxblood : (isActive ? HOUSE_DOCUMENT.accent : DOCUMENT.rule),
                    color: isDone || isActive ? '#F6EFE4' : DOCUMENT.ink3,
                    animation: isActive ? 'partnerAiSearchBeacon 1.4s ease-in-out infinite' : 'none'
                  }}
                >
                  {isDone ? '✓' : (step.pin || '·')}
                </Box>
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, fontWeight: isActive ? 600 : 500 }}>
                  {t(step.labelKey)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, whiteSpace: 'nowrap' }}>
                {stepStatusLabel(t, step.key, step.status, step.count)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: { xs: 140, md: 160 },
          borderRadius: 1,
          border: `1px solid ${DOCUMENT.rule}`,
          bgcolor: DOCUMENT.surface,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse at 30% 40%, ${HOUSE_DOCUMENT.accent}18 0%, transparent 55%),
              radial-gradient(ellipse at 70% 65%, ${HOUSE_DOCUMENT.oxblood}10 0%, transparent 50%),
              linear-gradient(160deg, ${DOCUMENT.ivory} 0%, ${DOCUMENT.surface} 55%, ${DOCUMENT.canvas} 100%)
            `
          }}
        />

        {[0, 1, 2].map((ring) => (
          <Box
            key={ring}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 36,
              height: 36,
              ml: '-18px',
              mt: '-18px',
              borderRadius: '50%',
              border: `1.5px solid ${HOUSE_DOCUMENT.oxblood}`,
              opacity: 0,
              animation: 'partnerAiSearchRing 2.4s ease-out infinite',
              animationDelay: `${ring * 0.8}s`
            }}
          />
        ))}

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '140%',
            height: '140%',
            ml: '-70%',
            mt: '-70%',
            background: `conic-gradient(from 0deg, transparent 0deg, ${HOUSE_DOCUMENT.accent}33 40deg, transparent 70deg)`,
            animation: 'partnerAiSearchSweep 3.2s linear infinite',
            transformOrigin: 'center'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 12,
            height: 12,
            ml: '-6px',
            mt: '-6px',
            borderRadius: '50%',
            bgcolor: HOUSE_DOCUMENT.oxblood,
            boxShadow: `0 0 0 4px ${HOUSE_DOCUMENT.oxblood}22`,
            animation: 'partnerAiSearchBeacon 1.6s ease-in-out infinite'
          }}
        />

        {STEP_META.filter((step) => step.pin).map((pin) => {
          const status = progress?.[pin.key] || 'pending';
          const isDone = status === 'done';
          const isActive = status === 'active';
          return (
            <Box
              key={pin.key}
              aria-hidden
              sx={{
                position: 'absolute',
                left: pin.x,
                top: pin.y,
                width: 26,
                height: 26,
                ml: '-13px',
                mt: '-13px',
                borderRadius: '50%',
                bgcolor: isDone || isActive ? pin.color : DOCUMENT.ruleHard,
                color: '#F6EFE4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                border: '2px solid #fff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                opacity: status === 'pending' ? 0.45 : 1,
                animation: isActive ? 'partnerAiSearchPin 1.8s ease-in-out infinite' : 'none',
                transition: 'background-color 0.25s ease, opacity 0.25s ease'
              }}
            >
              {isDone ? '✓' : pin.pin}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PartnerAiSearchingPanel;
